<template>
  <div class="search-overlay" :class="{ 'search-overlay--open': open }" @click.self="close">
    <div class="search-overlay__panel">
      <div class="search-overlay__head">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
        <input
          ref="inputRef"
          v-model="query"
          type="search"
          class="search-overlay__input"
          placeholder="搜索产品、分类、方案…"
          @keydown.esc="close"
        />
        <button type="button" class="search-overlay__close" aria-label="关闭" @click="close">×</button>
      </div>

      <div v-if="query.trim()" class="search-overlay__results">
        <p v-if="!results.length" class="search-overlay__empty">未找到「{{ query }}」相关结果</p>
        <button
          v-for="item in results"
          :key="`${item.categoryId}-${item.slug}`"
          type="button"
          class="search-result"
          @click="goResult(item)"
        >
          <img :src="item.image" :alt="item.name" class="search-result__img" />
          <div class="search-result__body">
            <strong>{{ item.name }}</strong>
            <span>{{ item.groupLabel }} · {{ item.categoryLabel }}</span>
          </div>
        </button>
      </div>
      <div v-else class="search-overlay__hints">
        <p>热门搜索</p>
        <div class="search-overlay__tags">
          <button
            v-for="tag in hotTags"
            :key="tag"
            type="button"
            @click="query = tag"
          >
            {{ tag }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { searchProducts } from '../data/productCatalog'
import { searchCustomProducts } from '../utils/customProductCatalog'

const props = defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['update:open'])

const router = useRouter()
const query = ref('')
const inputRef = ref(null)

const hotTags = ['智慧用电', '实训平台', 'AGV', '人型机器人', 'MES', '数字孪生']

const results = computed(() => {
  const q = query.value
  const builtIn = searchProducts(q)
  const custom = searchCustomProducts(q)
  const seen = new Set(builtIn.map((r) => `${r.categoryId}-${r.slug}`))
  const merged = [...builtIn]
  for (const item of custom) {
    const key = `${item.categoryId}-${item.slug}`
    if (!seen.has(key)) merged.push(item)
  }
  return merged.slice(0, 12)
})

watch(
  () => props.open,
  async (val) => {
    if (val) {
      query.value = ''
      await nextTick()
      inputRef.value?.focus()
    }
  }
)

function close() {
  emit('update:open', false)
}

function goResult(item) {
  close()
  router.push({
    name: 'product-detail',
    params: { categoryId: item.categoryId, productSlug: item.slug },
  })
}
</script>

<style scoped>
.search-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  background: rgba(8, 18, 40, 0.55);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 24px 24px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s var(--ease-smooth), visibility 0.3s;
}

.search-overlay--open {
  opacity: 1;
  visibility: visible;
}

.search-overlay__panel {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  transform: translateY(-16px);
  transition: transform 0.35s var(--ease-out-expo);
}

.search-overlay--open .search-overlay__panel {
  transform: translateY(0);
}

.search-overlay__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #eef2f7;
  color: var(--brand-primary);
}

.search-overlay__input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 17px;
  font-family: inherit;
  color: var(--text-primary);
}

.search-overlay__input::placeholder {
  color: #aaa;
}

.search-overlay__close {
  width: 36px;
  height: 36px;
  border: none;
  background: #f3f6fb;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #666;
}

.search-overlay__results {
  max-height: 420px;
  overflow-y: auto;
  padding: 8px;
}

.search-overlay__empty {
  margin: 0;
  padding: 32px 20px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
}

.search-result {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.2s;
}

.search-result:hover {
  background: rgba(43, 89, 195, 0.06);
}

.search-result__img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 8px;
  background: #f0f4fa;
  flex-shrink: 0;
}

.search-result__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.search-result__body strong {
  font-size: 15px;
  color: var(--text-primary);
}

.search-result__body span {
  font-size: 12px;
  color: var(--text-muted);
}

.search-overlay__hints {
  padding: 20px 24px 28px;
}

.search-overlay__hints p {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--text-muted);
}

.search-overlay__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-overlay__tags button {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.2s, color 0.2s;
}

.search-overlay__tags button:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
}
</style>
